import { first } from 'lodash';
import { Observable, of } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';
import { IMobizHttpService, IMobizStateService, SerializableObject } from '../../core';
import { Inject, Injectable, InjectionToken } from '../../injection';
import { IMobizSettingsService, MobizSettingsKeyRecentObject, RecentObjectsService } from '../../settings';

import { Notification } from '../data/notification.do';

export interface IPartialMobizNotification extends Pick<Notification, 'timeStampUtc'>, SerializableObject {
  timeStampUtc: Date;
}

export interface IRecentNotificationsService {
  /**
   * Clear the "RecentNotifications" state for all user memberships.
   */
  clearMostRecentlyViewedNotificationDates(): Observable<void>;
  /**
   * Get the "RecentNotifications" state for the {@see IMobizStateService.currentMembership|current membership}.
   */
  getMostRecentlyViewedNotificationDate(): Observable<Date | undefined>;
  /**
   * Update the "RecentNotifications" state for the {@see IMobizStateService.currentMembership|current membership}.
   */
  setMostRecentlyViewedNotificationDate(date: Date): Observable<void>;
}

export const RECENT_NOTIFICATIONS_SERVICE: InjectionToken<IRecentNotificationsService>
  = new InjectionToken<IRecentNotificationsService>('RECENT_NOTIFICATIONS_SERVICE');

@Injectable()
export class RecentNotificationsService extends RecentObjectsService<IPartialMobizNotification>
  implements IRecentNotificationsService {

  constructor(
    @Inject(IMobizHttpService)
    protected httpService: IMobizHttpService,
    @Inject(IMobizSettingsService)
    protected settingsService: IMobizSettingsService,
    @Inject(IMobizStateService)
    protected state: IMobizStateService,
  ) {
    super(settingsService, state);
  }

  public clearMostRecentlyViewedNotificationDates(): Observable<void> {
    return this.clearRecentObjects().pipe(
      mapTo(undefined),
    );
  }

  /**
   * Returns the date of the most recently viewed notification for the current organization.
   */
  public getMostRecentlyViewedNotificationDate(): Observable<Date | undefined> {
    return this.getRecentObjects().pipe(
      map((partialNotifications: IPartialMobizNotification[]): Date | undefined => {
        const partialNotification: IPartialMobizNotification | undefined = first(partialNotifications);
        if (partialNotification) {
          return partialNotification.timeStampUtc;
        }
        return undefined;
      }),
    );
  }

  public setMostRecentlyViewedNotificationDate(date: Date): Observable<void> {
    const mostRecentByOrganization: Record<string, string[]> = {};
    mostRecentByOrganization[this.organizationId] = [date.toISOString()];
    return this.updateRecentObjects(mostRecentByOrganization).pipe(
      mapTo(undefined),
    );
  }

  protected getKey(): MobizSettingsKeyRecentObject {
    return 'RecentNotifications';
  }

  protected getObjectProperty(object: IPartialMobizNotification): string {
    return `${object.timeStampUtc.getTime()}`;
  }

  protected getObjects(objectProperties: string[]): Observable<IPartialMobizNotification[]> {
    // There's no need to fetch the notification objects from the database, only the stored timestamp is used.
    const partialMobizNotifications = objectProperties.map((timestamp: string): IPartialMobizNotification => {
      return new Notification({ timeStampUtc: timestamp });
    });
    return of(partialMobizNotifications);
  }

  protected sortObjects(unsortedObjects: Notification[], _sortedProperties: string[]): Notification[] {
    // There's no need to sort, only one item is stored per organization.
    return unsortedObjects;
  }
}
